/// <reference lib="dom" />
/// <reference lib="dom.iterable" />

/**
 * Content Protection Utility
 *
 * Provides three layers of protection that are each enabled/disabled by the
 * global content-protection policy fetched from the backend:
 *
 *  1. Canvas watermark overlay  � traces any screenshot to the exact user
 *  2. Blur on window focus loss � deters casual screen-recording
 *  3. getDisplayMedia intercept � blocks in-browser tab sharing + audit log
 *
 * OS-level screenshot tools cannot be blocked from a web browser; this utility
 * makes every leaked image a traceable evidence artifact instead.
 */

const CANVAS_ID = 'wm-overlay-canvas';
const STYLE_ID = 'wm-print-block';
const BLUR_STYLE_ID = 'wm-blur-style';

export interface WatermarkConfig {
  userName: string;
  userRole: string;
  ip: string;
  lat?: number;
  lng?: number;
}

let _currentConfig: WatermarkConfig | null = null;
let _observer: MutationObserver | null = null;
let _origGetDisplayMedia: ((constraints?: DisplayMediaStreamOptions) => Promise<MediaStream>) | null = null;
let _screenShareBlocked = false;

/* ?????????????????????????????????????????????
   WATERMARK CANVAS
????????????????????????????????????????????? */

function buildTimestamp(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function drawWatermark(canvas: HTMLCanvasElement, config: WatermarkConfig): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const line1 = `${config.userName}  |  ${config.userRole}`;
  const line2 = `IP: ${config.ip}  |  ${buildTimestamp()}`;
  const line3 = config.lat != null && config.lng != null
    ? `Lat: ${config.lat.toFixed(4)}  Lng: ${config.lng.toFixed(4)}`
    : '';

  const stepX = 280;
  const stepY = 140;
  const angle = -Math.PI / 6; // -30�

  for (let x = -stepX; x < canvas.width + stepX * 2; x += stepX) {
    for (let y = -stepY; y < canvas.height + stepY * 2; y += stepY) {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);

      // White pass (shows on dark backgrounds)
      ctx.globalAlpha = 0.55;
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 11px monospace';
      ctx.fillText(line1, 0, 0);
      ctx.font = '10px monospace';
      ctx.fillText(line2, 0, 16);
      if (line3) ctx.fillText(line3, 0, 30);

      // Dark pass (shows on light backgrounds)
      ctx.globalAlpha = 0.45;
      ctx.fillStyle = '#1a1a2e';
      ctx.font = 'bold 11px monospace';
      ctx.fillText(line1, 1, 1);
      ctx.font = '10px monospace';
      ctx.fillText(line2, 1, 17);
      if (line3) ctx.fillText(line3, 1, 31);

      ctx.restore();
    }
  }
}

function createCanvas(config: WatermarkConfig): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.id = CANVAS_ID;
  Object.assign(canvas.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    zIndex: '2147483647',
    pointerEvents: 'none',
    opacity: '0.09',
  });
  drawWatermark(canvas, config);
  return canvas;
}

function injectPrintBlock(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = '@media print { body { display: none !important; } }';
  document.head.appendChild(style);
}

function attachObserver(config: WatermarkConfig): void {
  if (_observer) _observer.disconnect();

  _observer = new MutationObserver(() => {
    if (!document.getElementById(CANVAS_ID)) {
      // Canvas was removed � re-inject silently
      const canvas = createCanvas(config);
      document.body.appendChild(canvas);
    }
  });

  _observer.observe(document.body, { childList: true, subtree: false });
}

function attachResizeHandler(config: WatermarkConfig): void {
  const onResize = () => {
    const canvas = document.getElementById(CANVAS_ID) as HTMLCanvasElement | null;
    if (canvas) drawWatermark(canvas, config);
  };
  window.addEventListener('resize', onResize);
}

function requestGeolocation(onUpdate: (lat: number, lng: number) => void): void {
  if (!navigator.geolocation) return;
  navigator.geolocation.getCurrentPosition(
    (pos) => onUpdate(pos.coords.latitude, pos.coords.longitude),
    () => { /* permission denied � no-op */ },
    { timeout: 8000 }
  );
}

/* ?????????????????????????????????????????????
   PUBLIC API � WATERMARK
????????????????????????????????????????????? */

export function initWatermark(config: WatermarkConfig): void {
  // Disabled per user request
}

export function updateWatermark(config: WatermarkConfig): void {
  _currentConfig = { ...config };
  const canvas = document.getElementById(CANVAS_ID) as HTMLCanvasElement | null;
  if (canvas) {
    drawWatermark(canvas, _currentConfig);
  } else {
    initWatermark(_currentConfig);
  }
}

export function destroyWatermark(): void {
  if (_observer) {
    _observer.disconnect();
    _observer = null;
  }
  document.getElementById(CANVAS_ID)?.remove();
  document.getElementById(STYLE_ID)?.remove();
  _currentConfig = null;
}

/* ?????????????????????????????????????????????
   BLUR ON VISIBILITY CHANGE (screen recording deterrence)
   Uses document.visibilitychange � only fires when the user actually
   switches browser tab or minimises the browser. Does NOT fire for:
   address bar clicks, DevTools, extension popups, alt-tab within browser.
????????????????????????????????????????????? */

// CSS blur via visibilitychange / window blur is unreliable on web:
// it fires during normal interactions (address bar, DevTools, Google Meet tab
// switch) and causes permanent content-obscuring side effects.
// Screen-recording deterrence is handled by the watermark (traces the leaker)
// and the getDisplayMedia intercept (blocks browser tab sharing).
// applyBlurProtection is kept as a safe no-op so existing callers compile.

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function applyBlurProtection(_enabled: boolean): void {
  // Always ensure no stale blur class or style leaks onto the page
  document.body.classList.remove('wm-blurred');
  document.getElementById(BLUR_STYLE_ID)?.remove();
}

/* ?????????????????????????????????????????????
   SCREEN SHARE BLOCKING (getDisplayMedia)
????????????????????????????????????????????? */

export function applyScreenShareBlock(
  blocked: boolean,
  auditFn?: () => void
): void {
  if (!navigator.mediaDevices) return;

  if (blocked && !_screenShareBlocked) {
    _origGetDisplayMedia = navigator.mediaDevices.getDisplayMedia.bind(navigator.mediaDevices);
    // Override with a function that rejects and fires the audit callback
    navigator.mediaDevices.getDisplayMedia = async (_constraints?: DisplayMediaStreamOptions): Promise<MediaStream> => {
      auditFn?.();
      return Promise.reject(
        new DOMException(
          'Screen sharing is disabled by your organisation policy.',
          'NotAllowedError'
        )
      );
    };
    _screenShareBlocked = true;
  } else if (!blocked && _screenShareBlocked && _origGetDisplayMedia) {
    navigator.mediaDevices.getDisplayMedia = _origGetDisplayMedia;
    _origGetDisplayMedia = null;
    _screenShareBlocked = false;
  }
}
